import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

type ActiveHoverTip = {
  id: string;
  content: ReactNode;
  x: number;
  y: number;
  maxWidthClass: string;
  trigger: HTMLElement;
};

let activeTip: ActiveHoverTip | null = null;
let lastPointer = { x: 0, y: 0 };
const tipSubscribers = new Set<() => void>();
let documentListenersBound = false;

function emitTipChange() {
  tipSubscribers.forEach((listener) => listener());
}

function setActiveTip(next: ActiveHoverTip | null) {
  activeTip = next;
  emitTipChange();
}

function isPointerOverTrigger(trigger: HTMLElement, x: number, y: number) {
  const node = document.elementFromPoint(x, y);
  if (node && trigger.contains(node)) return true;

  const rect = trigger.getBoundingClientRect();
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

function closeIfPointerLeftTrigger(event: PointerEvent | MouseEvent) {
  lastPointer = { x: event.clientX, y: event.clientY };
  if (!activeTip) return;

  const target = event.target;
  if (target instanceof Node && activeTip.trigger.contains(target)) return;
  if (isPointerOverTrigger(activeTip.trigger, event.clientX, event.clientY)) return;

  setActiveTip(null);
}

function closeActiveTip() {
  if (!activeTip) return;
  setActiveTip(null);
}

function bindDocumentListeners() {
  if (documentListenersBound || typeof document === "undefined") return;
  documentListenersBound = true;

  document.addEventListener("pointermove", closeIfPointerLeftTrigger, true);
  document.addEventListener("pointerdown", closeIfPointerLeftTrigger, true);
  window.addEventListener("scroll", closeActiveTip, true);
  window.addEventListener("blur", closeActiveTip);
  document.addEventListener("mouseout", (event) => {
    if (event.relatedTarget == null) closeActiveTip();
  });
}

function subscribeToTip(onStoreChange: () => void) {
  bindDocumentListeners();
  tipSubscribers.add(onStoreChange);
  return () => {
    tipSubscribers.delete(onStoreChange);
  };
}

function getActiveTip() {
  return activeTip;
}

function getServerSnapshot(): ActiveHoverTip | null {
  return null;
}

export function closeAllHoverTips() {
  closeActiveTip();
}

export function HoverTip({
  content,
  children,
  maxWidthClass = "max-w-xs",
}: {
  content: ReactNode;
  children: ReactNode;
  maxWidthClass?: string;
}) {
  const id = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const current = useSyncExternalStore(
    subscribeToTip,
    getActiveTip,
    getServerSnapshot,
  );

  const show = () => {
    const trigger = triggerRef.current;
    if (!trigger || !content) return;

    const rect = trigger.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top - 8;

    if (
      activeTip?.id === id &&
      Math.abs(activeTip.x - x) < 0.5 &&
      Math.abs(activeTip.y - y) < 0.5
    ) {
      return;
    }

    setActiveTip({
      id,
      content,
      x,
      y,
      maxWidthClass,
      trigger,
    });
  };

  const hide = () => {
    if (activeTip?.id === id) setActiveTip(null);
  };

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger || !content) return;
    if (isPointerOverTrigger(trigger, lastPointer.x, lastPointer.y)) {
      show();
    }
  }, [id, content]);

  useEffect(() => {
    return () => {
      if (activeTip?.id === id) setActiveTip(null);
    };
  }, [id]);

  if (!content) return <>{children}</>;

  const isActive = current?.id === id;

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex"
        onPointerEnter={show}
        onPointerMove={show}
        onPointerLeave={hide}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {children}
      </span>
      {isActive && current && typeof document !== "undefined"
        ? createPortal(
            <div
              role="tooltip"
              className={`pointer-events-none fixed z-[100] -translate-x-1/2 -translate-y-full rounded-md bg-black px-3 py-1.5 text-sm text-white shadow-md ${current.maxWidthClass}`}
              style={{ left: current.x, top: current.y }}
            >
              {current.content}
              <span className="absolute left-1/2 top-full -translate-x-1/2 border-8 border-transparent border-t-black" />
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
